// src/components/Games/GoldenSevenSlots/GoldenSevenSlots.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gameService from '../../../services/gameService';
import {
  Button,
  Card,
  Spinner,
  InputGroup,
  FormControl,
  Modal,
  Alert,
  DropdownButton,
  Dropdown,
  Badge,
} from 'react-bootstrap';
import {
  FaPlay,
  FaHistory,
  FaInfoCircle,
  FaArrowLeft,
  FaTimesCircle,
  FaCoins,
  FaCrown,
  FaVolumeUp,
  FaVolumeMute,
  FaStop,
  FaSyncAlt,
  FaBolt, // icono rayo para Mega Spin
} from 'react-icons/fa';
import './GoldenSevenSlots.css';

const MAX_LINES = 3; // <- SOLO 3 LÍNEAS
const MIN_BET = 1;
const MAX_BET = 10000;

const GoldenSevenSlots = () => {
  const navigate = useNavigate();

  const [betAmount, setBetAmount] = useState(1);
  const [reels, setReels] = useState([[], [], [], [], []]); // 5 carretes x 3 filas
  const [winnings, setWinnings] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [features, setFeatures] = useState({}); // { freeSpins, megaSpin, jackpot, bonusGame }
  const [loading, setLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winningSymbols, setWinningSymbols] = useState([]); // ["reel-row", ...]
  const [winningLines, setWinningLines] = useState([]); // [1,2,3]
  const [selectedLines, setSelectedLines] = useState([1, 2, 3]); // <- por defecto 3 líneas activas
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [remainingAutoPlaySpins, setRemainingAutoPlaySpins] = useState(0);

  // Audio
  const spinSoundRef = useRef(null);
  const winSoundRef = useRef(null);
  const loseSoundRef = useRef(null);
  const startSoundRef = useRef(null);
  const autoPlayTimeoutRef = useRef(null);

  const reelSpinDurations = [500, 700, 900, 1100, 1300];

  const symbolsList = [
    'bar', 'bell', 'cherry', 'lemon', 'orange', 'plum', 'seven',
    'strawberry', 'watermelon', 'wild', 'scatter', 'bonus',
  ];

  const getSymbolImage = (symbol) => `${process.env.PUBLIC_URL}/images/slot/${symbol}.png`;

  const generateRandomReelSymbols = () =>
    Array.from({ length: 5 }, () =>
      Array.from({ length: 3 }, () =>
        symbolsList[Math.floor(Math.random() * symbolsList.length)]
      )
    );

  // Historial
  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await gameService.getGoldenSevenSlotsHistory();
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al obtener el historial');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showHistoryModal) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistoryModal]);

  // AutoPlay simple: dispara cada ~1s si no está girando
  useEffect(() => {
    if (isAutoPlay && !isSpinning && remainingAutoPlaySpins > 0) {
      autoPlayTimeoutRef.current = setTimeout(() => {
        playGame();
        setRemainingAutoPlaySpins((prev) => prev - 1);
      }, 1000);
    } else if (remainingAutoPlaySpins === 0) {
      setIsAutoPlay(false);
    }
    return () => {
      if (autoPlayTimeoutRef.current) clearTimeout(autoPlayTimeoutRef.current);
    };
  }, [isAutoPlay, isSpinning, remainingAutoPlaySpins]);

  // Toggle de líneas — SOLO 1..3
  const handleLineSelection = (lineNumber) => {
    if (lineNumber < 1 || lineNumber > MAX_LINES) return;
    setSelectedLines((prev) =>
      prev.includes(lineNumber)
        ? prev.filter((n) => n !== lineNumber)
        : [...prev, lineNumber]
    );
  };

  const startAutoPlay = (spins) => {
    if (!Number.isInteger(betAmount) || betAmount < MIN_BET || betAmount > MAX_BET) {
      setError(`Configura una apuesta entera válida entre ${MIN_BET} y ${MAX_BET}.`);
      return;
    }
    if (selectedLines.length === 0) {
      setError('Debes seleccionar al menos una línea de pago.');
      return;
    }
    setError(null);
    setRemainingAutoPlaySpins(spins);
    setIsAutoPlay(true);
  };

  const stopAutoPlay = () => {
    setIsAutoPlay(false);
    setRemainingAutoPlaySpins(0);
    if (autoPlayTimeoutRef.current) clearTimeout(autoPlayTimeoutRef.current);
  };

  const toggleSound = () => setIsSoundOn((s) => !s);

  const playGame = async () => {
    if (selectedLines.length === 0) {
      setError('Debes seleccionar al menos una línea de pago.');
      return;
    }
    if (!Number.isInteger(betAmount) || betAmount < MIN_BET || betAmount > MAX_BET) {
      setError(`La apuesta debe ser un entero entre ${MIN_BET} y ${MAX_BET}.`);
      return;
    }

    try {
      setLoading(true);
      setIsSpinning(true);
      setError(null);
      setWinningSymbols([]);
      setWinningLines([]);
      setWinnings(0);

      if (isSoundOn) {
        startSoundRef.current?.play();
        spinSoundRef.current?.play();
        setTimeout(() => {
          spinSoundRef.current?.pause();
          spinSoundRef.current.currentTime = 0;
        }, 2000);
      }

      // Animación de "giro"
      const spinningReels = generateRandomReelSymbols();
      setReels(spinningReels);

      // Llamada al backend
      const data = await gameService.playGoldenSevenSlots(betAmount, selectedLines.length);

      // Efecto de parada escalonada de carretes
      reelSpinDurations.forEach((duration, index) => {
        setTimeout(() => {
          setReels((prev) => {
            const next = [...prev];
            next[index] = data.reels[index];
            return next;
          });

          if (index === reelSpinDurations.length - 1) {
            setTimeout(() => {
              setIsSpinning(false);
              setLoading(false);
              setWinnings(data.winnings || 0);
              setCurrentBalance(
                typeof data.currentBalance === 'number' ? data.currentBalance : currentBalance
              );
              setFeatures(data.features || {});

              // Resaltar símbolos y líneas ganadoras
              if (Array.isArray(data.winLines) && data.winLines.length > 0) {
                const winningPositions = [];
                const winningLineNumbers = [];
                data.winLines.forEach(({ lineNumber, positions }) => {
                  if (lineNumber >= 1 && lineNumber <= MAX_LINES) {
                    winningLineNumbers.push(lineNumber);
                  }
                  positions.forEach(({ reel, row }) => {
                    winningPositions.push(`${reel}-${row}`);
                  });
                });
                setWinningSymbols(winningPositions);
                setWinningLines(winningLineNumbers);
              }

              if (isSoundOn) {
                if ((data.winnings || 0) > 0) winSoundRef.current?.play();
                else loseSoundRef.current?.play();
              }
            }, 500);
          }
        }, duration);
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Error al jugar');
      setLoading(false);
      setIsSpinning(false);
      setIsAutoPlay(false);
    }
  };

  return (
    <div className="GoldenSevenSlots-slots-container d-flex justify-content-center align-items-center">
      <Card className="text-center GoldenSevenSlots-slots-card shadow-lg">
        <Card.Body>
          {/* Header */}
          <div className="position-relative mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              className="GoldenSevenSlots-back-button"
              title="Volver"
            >
              <FaArrowLeft className="me-2" />
            </Button>

            <Button
              variant="secondary"
              onClick={toggleSound}
              className="GoldenSevenSlots-sound-button"
              title={isSoundOn ? 'Silenciar' : 'Activar sonido'}
            >
              {isSoundOn ? <FaVolumeUp /> : <FaVolumeMute />}
            </Button>

            <h1 className="GoldenSevenSlots-casino-title text-center">
              <FaCrown className="text-warning me-2" /> Golden Seven Slots{' '}
              <FaCrown className="text-warning ms-2" />
            </h1>
          </div>

          {error && <Alert variant="danger" className="mt-2">{error}</Alert>}

          {/* Apuesta */}
          <div className="mb-3">
            <InputGroup className="GoldenSevenSlots-bet-input-group mb-2">
              <InputGroup.Text className="GoldenSevenSlots-bet-input-icon">
                <FaCoins />
              </InputGroup.Text>

              <FormControl
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Math.trunc(Number(e.target.value) || 0))}
                min={MIN_BET}
                max={MAX_BET}
                className="GoldenSevenSlots-bet-input"
                disabled={isAutoPlay || isSpinning}
              />

              <InputGroup.Text className="GoldenSevenSlots-bet-input-text">
                MENEL por línea
              </InputGroup.Text>
            </InputGroup>
          </div>

          {/* Botones principales */}
          <div className="d-flex justify-content-around flex-wrap mb-3 gap-2">
            <Button
              className="GoldenSevenSlots-slots-button GoldenSevenSlots-play-button"
              onClick={playGame}
              disabled={loading || betAmount < MIN_BET || betAmount > MAX_BET || isSpinning || isAutoPlay}
            >
              {loading ? <Spinner animation="border" size="sm" /> : <><FaPlay className="me-2" /> Jugar</>}
            </Button>

            {isAutoPlay ? (
              <Button
                className="GoldenSevenSlots-slots-button GoldenSevenSlots-autoplay-button"
                onClick={stopAutoPlay}
                disabled={isSpinning}
              >
                <FaStop className="me-2" /> Detener Auto Play ({remainingAutoPlaySpins})
              </Button>
            ) : (
              <DropdownButton
                title={<><FaSyncAlt className="me-2" /> Auto Play</>}
                variant="success"
                disabled={isSpinning}
                className="GoldenSevenSlots-slots-button GoldenSevenSlots-autoplay-button"
              >
                {[10, 25, 50, 100].map((spins) => (
                  <Dropdown.Item key={spins} onClick={() => startAutoPlay(spins)}>
                    {spins} tiradas
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            )}

            <Button
              className="GoldenSevenSlots-slots-button GoldenSevenSlots-history-button"
              onClick={() => setShowHistoryModal(true)}
              disabled={isSpinning || isAutoPlay}
            >
              <FaHistory className="me-2" /> Historial
            </Button>

            <Button
              className="GoldenSevenSlots-slots-button GoldenSevenSlots-rules-button"
              onClick={() => setShowRulesModal(true)}
              disabled={isSpinning || isAutoPlay}
            >
              <FaInfoCircle className="me-2" /> Reglas
            </Button>
          </div>

          {/* Reels + 3 líneas únicamente */}
          <div className="GoldenSevenSlots-reels-container position-relative">
            {[0, 1, 2].map((rowIndex) => (
              <div
                key={rowIndex}
                className={`GoldenSevenSlots-line-container GoldenSevenSlots-line-${rowIndex + 1}`}
              >
                <Button
                  variant={selectedLines.includes(rowIndex + 1) ? 'primary' : 'outline-primary'}
                  className="GoldenSevenSlots-line-button"
                  onClick={() => handleLineSelection(rowIndex + 1)}
                  disabled={isSpinning || isAutoPlay}
                >
                  Línea {rowIndex + 1}
                </Button>

                <div
                  className={[
                    'GoldenSevenSlots-payline-overlay',
                    selectedLines.includes(rowIndex + 1) ? 'active' : '',
                    winningLines.includes(rowIndex + 1) ? 'blink-line' : '',
                  ].join(' ')}
                />
              </div>
            ))}

            <div className="GoldenSevenSlots-reels-wrapper">
              {[0, 1, 2, 3, 4].map((reelIndex) => (
                <div key={reelIndex} className="GoldenSevenSlots-reel">
                  <div className={`GoldenSevenSlots-reel-inner ${isSpinning ? 'spinning' : ''}`}>
                    {reels[reelIndex].length > 0
                      ? reels[reelIndex].map((symbol, idx) => {
                          const posKey = `${reelIndex}-${idx}`;
                          const isWinningSymbol = winningSymbols.includes(posKey);
                          return (
                            <img
                              key={idx}
                              src={getSymbolImage(symbol)}
                              alt={symbol}
                              className={`GoldenSevenSlots-slot-symbol ${isWinningSymbol ? 'blink' : ''}`}
                            />
                          );
                        })
                      : [0, 1, 2].map((idx) => (
                          <img
                            key={idx}
                            src={`${process.env.PUBLIC_URL}/images/slot/placeholder.png`}
                            alt="placeholder"
                            className="GoldenSevenSlots-slot-symbol placeholder"
                          />
                        ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Badges de features (opcionales) */}
          <div className="mt-3 d-flex gap-2 justify-content-center flex-wrap">
            {features?.freeSpins ? (
              <Badge bg="warning" text="dark">Free Spins: {features.freeSpins}x</Badge>
            ) : null}
            {features?.megaSpin ? (
              <Badge bg="info"><FaBolt className="me-1" /> Mega Spin</Badge>
            ) : null}
            {features?.jackpot ? (
              <Badge bg="danger">Jackpot</Badge>
            ) : null}
            {features?.bonusGame ? (
              <Badge bg="success">Bonus Game</Badge>
            ) : null}
          </div>

          {/* Ganancias / Saldo */}
          {!loading && !isSpinning && winnings > 0 && (
            <div className="mt-3">
              <h4 className="text-success GoldenSevenSlots-win-message">
                ¡Ganaste {winnings} MENEL! {features?.jackpot && '🎉 Jackpot!'}
              </h4>
            </div>
          )}
          {currentBalance !== null && (
            <div className="mt-2">
              <p><strong>Saldo Actual:</strong> {currentBalance} MENEL</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Audios */}
      <audio ref={spinSoundRef} src={`${process.env.PUBLIC_URL}/audio/number-slot.mp3`} />
      <audio ref={winSoundRef} src={`${process.env.PUBLIC_URL}/audio/win.wav`} />
      <audio ref={loseSoundRef} src={`${process.env.PUBLIC_URL}/audio/lose.wav`} />
      <audio ref={startSoundRef} src={`${process.env.PUBLIC_URL}/audio/start.mp3`} />

      {/* Modal: Reglas */}
      <Modal
        show={showRulesModal}
        onHide={() => setShowRulesModal(false)}
        centered
        className="GoldenSevenSlots-rules-modal"
      >
        <Modal.Header closeButton className="GoldenSevenSlots-modal-header-custom">
          <Modal.Title><FaInfoCircle className="me-2" /> Reglas del Juego</Modal.Title>
        </Modal.Header>
        <Modal.Body className="GoldenSevenSlots-modal-body-custom">
          <p><strong>Golden Seven Slots</strong> es una tragamonedas de 5 carretes con <strong>3 líneas</strong> (superior, media, inferior).</p>
          <ul>
            <li>Ajusta la apuesta (MENEL por línea) y elige cuántas líneas activar (1 a 3).</li>
            <li><strong>Wild</strong> sustituye cualquier símbolo (excepto Scatter/Bonus).</li>
            <li>3+ <strong>Scatter</strong> → Free Spins (multiplicador).</li>
            <li>5 <strong>Seven</strong> en una línea → <strong>Jackpot</strong>.</li>
            <li>Si no hay líneas ganadoras, puede activarse <strong>Mega Spin</strong>.</li>
          </ul>
        </Modal.Body>
        <Modal.Footer className="GoldenSevenSlots-modal-footer-custom">
          <Button variant="secondary" onClick={() => setShowRulesModal(false)}>
            <FaTimesCircle className="me-2" /> Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Historial */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        centered
        className="GoldenSevenSlots-history-modal"
      >
        <Modal.Header closeButton className="GoldenSevenSlots-modal-header-custom">
          <Modal.Title><FaHistory className="me-2" /> Historial de Juego</Modal.Title>
        </Modal.Header>
        <Modal.Body className="GoldenSevenSlots-modal-body-custom">
          {loading ? <Spinner animation="border" /> : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ul className="list-group">
                {history.map((entry, index) => (
                  <li key={index} className="list-group-item">
                    <strong>Resultado:</strong> {entry.result}
                    <br />
                    <strong>Ganancia/Pérdida:</strong> {entry.score} MENEL
                    <br />
                    <strong>Fecha:</strong> {new Date(entry.created_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="GoldenSevenSlots-modal-footer-custom">
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            <FaTimesCircle className="me-2" /> Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GoldenSevenSlots;
